import React from "react";
import { MapPin, Clock, Wifi, Zap, Loader2, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { formatSalary } from "./JobCard";
import { CompanyAvatar } from "./CompanyAvatar";
import { jobTypeLabel, experienceLabel } from "../config/jobTaxonomy";

const PERIOD_KEY = { hour: "perHour", month: "perMonth", year: "perYear" };

/** Vista previa exacta de lo que el candidato verá — sin crear/publicar el empleo hasta confirmar. */
export function JobPreviewCard({ jobForm, company, isEdit, publishing, error, onBack, onPublish }) {
  const { t } = useLanguage();
  const location = jobForm.city
    ? [jobForm.city, jobForm.admin1_name].filter(Boolean).join(", ")
    : jobForm.location;
  const isConfidential = jobForm.company_visibility === "hidden";
  const companyLabel = isConfidential ? t("postJobLabels.confidentialCompanyLabel") : (company?.name || "");

  return (
    <div data-testid="job-preview-card">
      <p className="text-xs font-semibold text-[#F55FA5] uppercase tracking-widest mb-3">{t("postJobLabels.previewSectionTitle")}</p>

      <div className="rounded-2xl overflow-hidden border border-slate-200">
        <div className="p-5 bg-gradient-to-br from-[#028CF8] to-[#0270C5]">
          <div className="flex items-center gap-3">
            {!isConfidential && <CompanyAvatar name={company?.name} logo={company?.logo_url} size={44} />}
            <div className="min-w-0">
              <p className="text-white/90 text-sm font-semibold truncate" data-testid="preview-company-name">{companyLabel}</p>
              <h3 className="text-white font-poppins font-bold text-lg leading-tight truncate" data-testid="preview-job-title">{jobForm.title || t("postJobLabels.titleLabel")}</h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {location && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/15 text-white flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {location}
              </span>
            )}
            {jobForm.work_mode === "hybrid" && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/15 text-white">{t("postJobLabels.workModeHybrid")}</span>
            )}
            {jobForm.work_mode === "remote" && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/15 text-white flex items-center gap-1">
                <Wifi className="h-3 w-3" /> {t("postJobLabels.workModeRemote")}
              </span>
            )}
            {jobForm.urgency !== "normal" && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/15 text-white flex items-center gap-1">
                <Zap className="h-3 w-3" /> {jobForm.urgency === "urgent" ? t("postJobLabels.urgencyUrgent") : t("postJobLabels.urgencyImmediate")}
              </span>
            )}
          </div>
        </div>

        <div className="p-5 space-y-4 bg-white">
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
            <span>{jobTypeLabel(jobForm.job_type, t)}</span>
            <span>{experienceLabel(jobForm.experience_level, t)}</span>
            {jobForm.work_mode === "remote" && jobForm.remote_countries.length > 0 && (
              <span data-testid="preview-remote-countries">
                {t("jobDetail.remoteFromLabel")}: {jobForm.remote_countries.map((c) => t(`geo.countryNames.${c}`)).join(", ")}
              </span>
            )}
          </div>

          <div data-testid="preview-salary">
            {jobForm.salary_visible && (jobForm.salary_min || jobForm.salary_max) ? (
              <p className="text-[#028CF8] font-bold">
                {formatSalary(jobForm.salary_min, jobForm.salary_max, jobForm.salary_currency, t("jobCard.upTo"))}
                <span className="text-slate-400 font-normal text-sm ml-1">{t(`jobCard.${PERIOD_KEY[jobForm.salary_period] || "perYear"}`)}</span>
              </p>
            ) : (
              <p className="text-slate-400 text-sm italic">{t("postJobLabels.previewSalaryHidden")}</p>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t("postJobLabels.descriptionLabel")}</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{jobForm.description}</p>
          </div>

          {jobForm.requirements.filter(Boolean).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t("postJobLabels.requirementsLabel")}</p>
              <ul className="text-sm text-slate-700 space-y-1 list-disc pl-4">
                {jobForm.requirements.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {jobForm.benefits.filter(Boolean).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t("postJobLabels.benefitsLabel")}</p>
              <ul className="text-sm text-slate-700 space-y-1 list-disc pl-4">
                {jobForm.benefits.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-slate-600 pt-2 border-t border-slate-100">
            <CheckCircle2 className="h-4 w-4 text-[#028CF8] flex-shrink-0" />
            {jobForm.application_method === "external" ? t("postJobLabels.applyMethodExternal") : t("postJobLabels.applyMethodInternal")}
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mt-4" data-testid="preview-publish-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 mt-5">
        <button type="button" onClick={onPublish} disabled={publishing} data-testid="confirm-publish-button"
          className="w-full sm:flex-1 h-[52px] sm:h-12 bg-[#028CF8] hover:bg-[#0270C5] text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm">
          {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? t("postJobLabels.saveChangesFinalBtn") : t("postJobLabels.publishFinalBtn")}
        </button>
        <button type="button" onClick={onBack} disabled={publishing} data-testid="back-to-edit-button"
          className="w-full sm:flex-1 h-[52px] sm:h-12 border border-slate-200 bg-white text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-all text-sm flex items-center justify-center gap-2">
          <ArrowLeft className="h-4 w-4" /> {t("postJobLabels.backToEditBtn")}
        </button>
      </div>
    </div>
  );
}

export default JobPreviewCard;
